import { useTranslation } from 'react-i18next';
/**
 * RichTextEditor — thin wrapper around Tiptap for contract-template
 * paragraph bodies. Emits sanitised HTML on every change. Supports the
 * subset of formatting that the PDF renderer (WeasyPrint) handles well:
 *   • bold / italic / underline / strike
 *   • ordered & unordered lists
 *   • text alignment (via the paragraph-level toggle above the editor —
 *     we intentionally don't ship the Tiptap TextAlign extension here to
 *     keep alignment stored on the paragraph object, not in the HTML)
 *   • blockquote, code
 *
 * Deliberately NO tables, images or headings — those are handled at the
 * template level (headings) or as separate blocks (logos/tables) so the
 * PDF pipeline stays predictable.
 */

import React, { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Code, Undo, Redo, Link2, Link2Off,
} from 'lucide-react';
import { Button } from './ui/button';

// Small toolbar button so the toolbar stays visually compact.
const TBtn = ({ onClick, active, disabled, title, children, testid }) => (
  <Button
    type="button"
    size="icon"
    variant={active ? 'default' : 'ghost'}
    disabled={disabled}
    onClick={onClick}
    title={title}
    data-testid={testid}
    className="h-7 w-7"
  >
    {children}
  </Button>
);

export const RichTextEditor = ({ value, onChange, placeholder, testid, align }) => {
  const { t } = useTranslation();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,            // headings handled at paragraph level
        codeBlock: false,          // avoid full-block code; inline `code` still ok
      }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[6rem] px-3 py-2 focus:outline-none',
        'data-testid': testid || 'rich-text-editor',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Tiptap emits '<p></p>' for empty documents — normalise to '' so
      // the paragraph list treats it as blank.
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Keep external value changes (e.g. loading a new template) in sync.
  useEffect(() => {
    if (!editor) return;
    if ((value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL (leave empty to remove)', previous);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  return (
    <div className="rounded-md border bg-white" style={{ textAlign: align || 'justify' }}>
      <div className="flex flex-wrap items-center gap-0.5 border-b px-1 py-1 bg-slate-50">
        <TBtn testid="rte-bold" title={t('auto.misc2.bold')} onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}>
          <Bold className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn testid="rte-italic" title={t('auto.misc2.italic')} onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}>
          <Italic className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn testid="rte-underline" title={t('auto.misc2.underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')}>
          <UnderlineIcon className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn testid="rte-strike" title={t('auto.misc2.strikethrough')} onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')}>
          <Strikethrough className="h-3.5 w-3.5" />
        </TBtn>
        <span className="mx-1 h-4 w-px bg-slate-300" />
        <TBtn testid="rte-bullet" title={t('auto.misc2.bulletedList')} onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}>
          <List className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn testid="rte-ordered" title={t('auto.misc2.numberedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}>
          <ListOrdered className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn testid="rte-quote" title={t('auto.misc2.blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')}>
          <Quote className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn testid="rte-code" title={t('auto.misc2.inlineCode')} onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')}>
          <Code className="h-3.5 w-3.5" />
        </TBtn>
        <span className="mx-1 h-4 w-px bg-slate-300" />
        <TBtn testid="rte-link" title={t('auto.misc2.addEditLink')} onClick={setLink} active={editor.isActive('link')}>
          <Link2 className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn testid="rte-unlink" title={t('auto.misc2.removeLink')} onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive('link')}>
          <Link2Off className="h-3.5 w-3.5" />
        </TBtn>
        <span className="mx-1 h-4 w-px bg-slate-300" />
        <TBtn testid="rte-undo" title={t('auto.misc2.undo')} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo className="h-3.5 w-3.5" />
        </TBtn>
        <TBtn testid="rte-redo" title={t('auto.misc2.redo')} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo className="h-3.5 w-3.5" />
        </TBtn>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
};

export default RichTextEditor;
